import { useMemo, useState } from "react";
import { H, MANROPE, FOCUS } from "./hcTokens";

/* The one Health Check form — Sections A–F for clients, A–G for staff.
   It holds no scoring logic; the score comes back from EQUIPR Intelligence. */

const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: H.ink3 };
const INPUT = `w-full h-12 rounded-[9px] px-3 text-[16px] bg-white`;
const EDGE = { border: `1px solid ${H.control}`, color: H.ink };
const AREA = `w-full rounded-[9px] px-3 py-2.5 text-[16px] bg-white`;

export const Field = ({ label, hint, children, required }) => (
  <div className="mb-6">
    <div className="flex items-baseline gap-2 flex-wrap">
      <div className="text-[15px] font-semibold" style={{ color: H.ink }}>
        {label}{required && <span style={{ color: "#C23A00" }}> *</span>}
      </div>
    </div>
    {hint && <div className="text-[13px] mt-0.5 mb-1.5" style={{ color: H.ink2 }}>{hint}</div>}
    <div className="mt-2">{children}</div>
  </div>
);

export const Text = ({ value, onChange, placeholder, type = "text", testid }) => (
  <input type={type} value={value || ""} placeholder={placeholder} data-testid={testid}
    onChange={(e) => onChange(e.target.value)} className={`${INPUT} ${FOCUS}`} style={EDGE} />
);

export const Area = ({ value, onChange, rows = 4, placeholder, testid }) => (
  <textarea rows={rows} value={value || ""} placeholder={placeholder} data-testid={testid}
    onChange={(e) => onChange(e.target.value)} className={`${AREA} ${FOCUS}`} style={EDGE} />
);

export const Choice = ({ options, value, onChange, testid, multi = false, columns = 1 }) => {
  const sel = multi ? (value || []) : value;
  const on = (o) => {
    if (!multi) return onChange(o);
    const next = (value || []).includes(o) ? (value || []).filter((x) => x !== o) : [...(value || []), o];
    onChange(next);
  };
  const active = (o) => (multi ? sel.includes(o) : sel === o);
  return (
    <div className={`grid gap-2 ${columns === 2 ? "sm:grid-cols-2" : ""}`} data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => on(o)} data-testid={`${testid}-${o.slice(0, 18).replace(/\s+/g, "-").toLowerCase()}`}
          aria-pressed={active(o)}
          className={`min-h-[48px] rounded-[9px] px-3.5 py-2.5 text-left text-[15px] transition-colors duration-150 ${FOCUS}`}
          style={{
            background: active(o) ? H.accent : "#fff",
            border: `1px solid ${active(o) ? H.accentEdge : H.control}`,
            color: active(o) ? "#111" : H.ink,
            fontWeight: active(o) ? 700 : 500,
          }}>
          {o}
        </button>
      ))}
    </div>
  );
};

export const Scale = ({ from = 1, to = 5, value, onChange, lowLabel, highLabel, testid }) => {
  const nums = [];
  for (let i = from; i <= to; i++) nums.push(i);
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {nums.map((n) => {
          const on = Number(value) === n;
          return (
            <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`}
              aria-pressed={on}
              className={`w-[48px] h-[48px] rounded-[9px] text-[16px] transition-colors duration-150 ${FOCUS}`}
              style={{
                background: on ? H.accent : "#fff",
                border: `1px solid ${on ? H.accentEdge : H.control}`,
                color: on ? "#111" : H.ink, fontWeight: on ? 800 : 600,
              }}>{n}</button>
          );
        })}
      </div>
      {(lowLabel || highLabel) && (
        <div className="flex justify-between mt-2 text-[12.5px]" style={{ color: H.ink3 }}>
          <span>{lowLabel}</span><span>{highLabel}</span>
        </div>
      )}
    </div>
  );
};

const SectionHead = ({ eyebrow, title, blurb }) => (
  <div className="mb-6">
    <div style={{ ...LABEL, color: H.accentInk }}>{eyebrow}</div>
    <h2 className="mt-1" style={{ ...MANROPE, fontSize: 22, fontWeight: 800, letterSpacing: "-0.015em", color: H.ink }}>{title}</h2>
    {blurb && <div className="text-[14px] mt-1.5" style={{ color: H.ink2 }}>{blurb}</div>}
  </div>
);

const PainRow = ({ idx, row, meta, onChange, onRemove }) => (
  <div className="rounded-[11px] p-4 mb-4" style={{ background: H.panel, border: `1px solid ${H.divider}` }}
    data-testid={`pain-row-${idx}`}>
    <div className="flex items-center justify-between mb-2">
      <div style={LABEL}>Frustration {idx + 1}</div>
      {idx > 0 && (
        <button type="button" onClick={onRemove} data-testid={`pain-remove-${idx}`}
          className="text-[12.5px] underline" style={{ color: H.ink2 }}>Remove</button>
      )}
    </div>
    <Field label="What's causing frustration for your team right now?">
      <Area value={row.text} onChange={(v) => onChange({ ...row, text: v })} rows={3}
        testid={`pain-text-${idx}`} />
    </Field>
    {(row.text || "").trim() && (
      <>
        <Field label="Which product does it relate to?" required>
          <Choice options={meta.pain_products} value={row.product} columns={2}
            onChange={(v) => onChange({ ...row, product: v })} testid={`pain-product-${idx}`} />
        </Field>
        <Field label="How much does it affect you?" required>
          <Choice options={meta.severity_options} value={row.severity}
            onChange={(v) => onChange({ ...row, severity: v })} testid={`pain-severity-${idx}`} />
        </Field>
        <Field label="How do you work around it today?">
          <Text value={row.workaround} onChange={(v) => onChange({ ...row, workaround: v })}
            testid={`pain-workaround-${idx}`} />
        </Field>
      </>
    )}
  </div>
);

const FeatureRow = ({ f, meta, answer, walkthrough, onAnswer, onWalkthrough }) => (
  <div className="py-4" style={{ borderTop: `1px solid ${H.divider}` }} data-testid={`feature-${f.id}`}>
    <div className="text-[15px] font-semibold mb-2.5" style={{ color: H.ink }}>{f.label}</div>
    <Choice options={Object.values(meta.feature_answers)} value={meta.feature_answers[answer]}
      testid={`feature-${f.id}-answer`}
      onChange={(label) => {
        const key = Object.keys(meta.feature_answers).find((k) => meta.feature_answers[k] === label);
        onAnswer(key);
      }} />
    {answer === "didnt_know" && (
      <label className="flex items-center gap-2.5 mt-3 text-[14px] cursor-pointer" style={{ color: H.ink }}>
        <input type="checkbox" checked={!!walkthrough} data-testid={`feature-${f.id}-walkthrough`}
          onChange={(e) => onWalkthrough(e.target.checked)}
          className="w-5 h-5" style={{ accentColor: H.accentEdge }} />
        Want a quick 15-minute walkthrough?
      </label>
    )}
  </div>
);

export const HealthCheckForm = ({ mode, clientId, companyName, meta, a, setA,
  onSubmit, busy, savedAt, problems }) => {
  const [step, setStep] = useState(0);
  const set = (k) => (v) => setA({ ...a, [k]: v });
  const products = a.A7 || [];
  const features = useMemo(
    () => (meta.features || []).filter((f) => products.includes(f.product)),
    [meta.features, products]);

  const discover = meta.discover_features || [];

  const steps = useMemo(() => {
    const s = ["A", "B", "C"];
    if (features.length) s.push("D");
    if (discover.length) s.push("DK");
    s.push("E");
    if (mode === "client") s.push("F");
    else s.push("G");
    return s;
  }, [features.length, discover.length, mode]);

  const pains = a.pain_points || [{}];
  const setPain = (i, row) => {
    const next = [...pains]; next[i] = row; setA({ ...a, pain_points: next });
  };
  const featureAnswer = (id) => (a.features || {})[id];
  const setFeature = (id, v) => setA({ ...a, features: { ...(a.features || {}), [id]: v } });
  const setWalk = (id, v) => setA({ ...a, walkthrough: { ...(a.walkthrough || {}), [id]: v } });
  const discoverAnswer = (id) => (a.discover || {})[id];
  const setDiscover = (id, v) => setA({ ...a, discover: { ...(a.discover || {}), [id]: v } });
  const setDiscoverWalk = (id, v) =>
    setA({ ...a, discover_walkthrough: { ...(a.discover_walkthrough || {}), [id]: v } });

  const current = steps[step];
  const pct = Math.round(((step + 1) / steps.length) * 100);
  const last = step === steps.length - 1;

  return (
    <div data-testid="health-check-form">
      <div className="h-[3px] w-full rounded-full overflow-hidden mb-7" style={{ background: H.divider }}>
        <div className="h-full transition-all duration-500" data-testid="hc-progress"
          style={{ width: `${pct}%`, background: H.accentEdge }} />
      </div>
      <div className="flex items-center justify-between mb-5 text-[12.5px]" style={{ color: H.ink3 }}>
        <span data-testid="hc-step-count">Step {step + 1} of {steps.length}</span>
        {savedAt && <span data-testid="hc-saved">Saved — you can come back to this link</span>}
      </div>

      {current === "A" && (
        <div>
          <SectionHead eyebrow="Section A" title="About you"
            blurb="So we know who we're hearing from." />
          <Field label="Company">
            <div className="rounded-[9px] px-3 py-3 text-[15px] font-semibold"
              style={{ background: H.sunken, border: `1px solid ${H.divider}`, color: H.ink }}
              data-testid="hc-company">
              {companyName} <span className="mono text-[12.5px] font-normal" style={{ color: H.ink2 }}>{clientId}</span>
            </div>
          </Field>
          <Field label="Your name" required><Text value={a.A2} onChange={set("A2")} testid="hc-a2" /></Field>
          <Field label="Your role" required><Text value={a.A3} onChange={set("A3")} testid="hc-a3" /></Field>
          <Field label="Email" required><Text type="email" value={a.A4} onChange={set("A4")} testid="hc-a4" /></Field>
          <Field label="Phone"><Text value={a.A5} onChange={set("A5")} testid="hc-a5" /></Field>
          <Field label="Which best describes your business?" required>
            <Choice options={meta.business_types} value={a.A6} onChange={set("A6")} testid="hc-a6" columns={2} />
          </Field>
          <Field label="Which EQUIPR products do you use?" required
            hint="We've ticked what's on your account — change it if that's not right.">
            <Choice options={meta.products} value={a.A7} multi onChange={set("A7")} testid="hc-a7" columns={2} />
          </Field>
        </div>
      )}

      {current === "B" && (
        <div>
          <SectionHead eyebrow="Section B" title="Satisfaction"
            blurb="Be as straight with us as you like — it's the only way this is useful." />
          <Field label="How satisfied are you with our software?" required>
            <Scale from={1} to={5} value={a.B1} onChange={set("B1")} testid="hc-b1"
              lowLabel="1 Very dissatisfied" highLabel="5 Very satisfied" />
          </Field>
          <Field label="How satisfied are you with our service and support team?" required>
            <Scale from={1} to={5} value={a.B2} onChange={set("B2")} testid="hc-b2"
              lowLabel="1 Very dissatisfied" highLabel="5 Very satisfied" />
          </Field>
          <Field label="How likely are you to recommend EQUIPR to a business like yours?" required>
            <Scale from={0} to={10} value={a.B3} onChange={set("B3")} testid="hc-b3"
              lowLabel="0 Not at all likely" highLabel="10 Extremely likely" />
          </Field>
          <Field label="What's one thing we could do better?">
            <Area value={a.B4} onChange={set("B4")} testid="hc-b4" />
          </Field>
        </div>
      )}

      {current === "C" && (
        <div>
          <SectionHead eyebrow="Section C" title="Pain points"
            blurb="Up to three. The more specific, the more we can do about it." />
          {pains.map((row, i) => (
            <PainRow key={i} idx={i} row={row} meta={meta} onChange={(r) => setPain(i, r)}
              onRemove={() => setA({ ...a, pain_points: pains.filter((_, j) => j !== i) })} />
          ))}
          {pains.length < 3 && (
            <button type="button" data-testid="pain-add"
              onClick={() => setA({ ...a, pain_points: [...pains, {}] })}
              className={`h-11 px-4 rounded-[9px] text-[14px] font-semibold ${FOCUS}`}
              style={{ background: "#fff", border: `1px solid ${H.control}`, color: H.ink2 }}>
              Add another frustration
            </button>
          )}
          <div className="mt-7">
            <Field label="Where are you still using whiteboards, spreadsheets or other systems to get something done?">
              <Area value={a.C5} onChange={set("C5")} testid="hc-c5" />
            </Field>
          </div>
        </div>
      )}

      {current === "D" && (
        <div>
          <SectionHead eyebrow="Section D" title="Product and features"
            blurb="If something here is new to you, that's useful for us to know." />
          {features.map((f) => (
            <FeatureRow key={f.id} f={f} meta={meta} answer={featureAnswer(f.id)}
              walkthrough={(a.walkthrough || {})[f.id]}
              onAnswer={(v) => setFeature(f.id, v)} onWalkthrough={(v) => setWalk(f.id, v)} />
          ))}
          <div className="mt-7">
            <Field label="Is there anything you wish our software did that it doesn't today?">
              <Area value={a.D6} onChange={set("D6")} testid="hc-d6" />
            </Field>
          </div>
        </div>
      )}

      {current === "DK" && (
        <div>
          <SectionHead eyebrow="Did you know?" title="A few things people often miss"
            blurb="These aren't part of the core system, so plenty of teams have never seen them. Nothing here changes your score — we just want to know what you've heard of." />
          {discover.map((f) => (
            <FeatureRow key={f.id} f={f} meta={meta} answer={discoverAnswer(f.id)}
              walkthrough={(a.discover_walkthrough || {})[f.id]}
              onAnswer={(v) => setDiscover(f.id, v)}
              onWalkthrough={(v) => setDiscoverWalk(f.id, v)} />
          ))}
        </div>
      )}

      {current === "E" && (
        <div>
          <SectionHead eyebrow="Section E" title="Looking ahead" />
          <Field label="What's changing in your business in the next 6 to 12 months?">
            <Choice options={meta.e1_options} value={a.E1} multi onChange={set("E1")} testid="hc-e1" columns={2} />
            <div className="mt-3">
              <Text value={a.E1_other} onChange={set("E1_other")} placeholder="Anything else worth knowing" testid="hc-e1-other" />
            </div>
          </Field>
          <Field label="Are you looking at other software, or building your own tools, for anything we cover?" required>
            <Choice options={["No", "Not sure", "Yes"]} value={a.E2} onChange={set("E2")} testid="hc-e2" />
          </Field>
          <Field label="Has there been a change in who manages or uses EQUIPR at your business?" required>
            <Choice options={["No", "Yes"]} value={a.E3} onChange={set("E3")} testid="hc-e3" />
          </Field>
        </div>
      )}

      {current === "F" && (
        <div>
          <SectionHead eyebrow="Section F" title="Follow-up" />
          <Field label="Would you like a call to talk any of this through?" required>
            <Choice options={["Yes", "No thanks"]} value={a.F1} onChange={set("F1")} testid="hc-f1" />
          </Field>
          {a.F1 === "Yes" && (
            <Field label="Best time to reach you" required>
              <Choice options={["Morning", "Afternoon", "Any time"]} value={a.F2} onChange={set("F2")} testid="hc-f2" />
            </Field>
          )}
          <Field label="Privacy" required>
            <label className="flex items-start gap-3 text-[14px] cursor-pointer" style={{ color: H.ink }}>
              <input type="checkbox" checked={!!a.F3} data-testid="hc-f3"
                onChange={(e) => setA({ ...a, F3: e.target.checked })}
                className="w-5 h-5 mt-0.5" style={{ accentColor: H.accentEdge }} />
              <span>
                I agree to EQUIPR holding and using my answers to improve its software and service.
                {" "}
                <a href={meta.privacy_policy_url} target="_blank" rel="noreferrer"
                  style={{ color: H.accentInk, fontWeight: 700 }} data-testid="hc-privacy-link">
                  Read the privacy policy
                </a>.
              </span>
            </label>
          </Field>
        </div>
      )}

      {current === "G" && (
        <div>
          <SectionHead eyebrow="Section G" title="Staff only"
            blurb="The client never sees this section or the score." />
          <div className="grid sm:grid-cols-2 gap-x-5">
            <Field label="Completed by" required><Text value={a.G1} onChange={set("G1")} testid="hc-g1" /></Field>
            <Field label="Call date" required><Text type="date" value={a.G2} onChange={set("G2")} testid="hc-g2" /></Field>
          </div>
          <Field label="Tier" required hint="A is checked monthly, B every three months, C every six.">
            <Choice options={meta.tiers} value={a.G3} onChange={set("G3")} testid="hc-g3" />
          </Field>
          <Field label="Staff observations"><Area value={a.G4} onChange={set("G4")} testid="hc-g4" /></Field>
          <Field label="Our commitment (one, specific)" required>
            <Text value={a.G5} onChange={set("G5")} testid="hc-g5" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-x-5">
            <Field label="Commitment owner" required><Text value={a.G6} onChange={set("G6")} testid="hc-g6" /></Field>
            <Field label="Commitment due date" required><Text type="date" value={a.G7} onChange={set("G7")} testid="hc-g7" /></Field>
          </div>
          <Field label="Override health band" required
            hint="The calculated score and band are always kept. An override needs a reason.">
            <Choice options={["No override", "Green", "Amber", "Red"]} value={a.G8 || "No override"}
              onChange={set("G8")} testid="hc-g8" columns={2} />
          </Field>
          {(a.G8 || "No override") !== "No override" && (
            <Field label="Override reason" required>
              <Text value={a.G9} onChange={set("G9")} testid="hc-g9" />
            </Field>
          )}
        </div>
      )}

      {problems?.length > 0 && (
        <div className="rounded-[9px] px-4 py-3 mb-4 text-[13.5px]" data-testid="hc-problems"
          style={{ background: "#FFE4DA", border: "1px solid #F3BFA8", color: "#8E1212" }}>
          {problems.join(" ")}
        </div>
      )}

      <div className="flex items-center gap-3 pt-6 mt-2" style={{ borderTop: `1px solid ${H.divider}` }}>
        {step > 0 && (
          <button type="button" onClick={() => setStep(step - 1)} data-testid="hc-back"
            className={`h-12 px-4 rounded-[9px] text-[14px] font-semibold ${FOCUS}`}
            style={{ background: "#fff", border: `1px solid ${H.control}`, color: H.ink2 }}>Back</button>
        )}
        {!last ? (
          <button type="button" onClick={() => setStep(step + 1)} data-testid="hc-next"
            className={`ml-auto h-12 px-6 rounded-[9px] text-[14px] font-bold uppercase ${FOCUS}`}
            style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
            Continue
          </button>
        ) : (
          <button type="button" onClick={onSubmit} disabled={busy} data-testid="hc-submit"
            className={`ml-auto h-12 px-6 rounded-[9px] text-[14px] font-bold uppercase disabled:opacity-60 ${FOCUS}`}
            style={{ background: H.accent, border: `1px solid ${H.accentEdge}`, color: "#111", letterSpacing: "0.05em" }}>
            {busy ? "Sending…" : mode === "client" ? "Send my feedback" : "Submit and score"}
          </button>
        )}
      </div>
    </div>
  );
};

export default HealthCheckForm;
